import { createSlice } from '@reduxjs/toolkit'

import { TestFilterType } from '~/tests/types/TestPrep.types'
import { ExamResults, PracticeExercise } from '~/tests/types/exams.types'
import { AppLanguage } from '~/types/locale'

export interface TestPrepSliceType {
  filterData: TestFilterType | null
  id: number | null
  testId: string | null
  questions: PracticeExercise[] | []
  results: ExamResults[] | []
  // Language of the results currently shown — set when viewing a past test, whose exercises
  // may be in a different language than the user's current curriculum
  resultsLanguage: AppLanguage | null
  xp: number
  currentQuestionIndex: number
  shouldShowTimeTracker: boolean
  startTime: number
  calculatorHistory: Array<{ expression: string; result: number }>
}
const initialState: TestPrepSliceType = {
  filterData: null,
  id: null,
  testId: null,
  questions: [],
  results: [],
  resultsLanguage: null,
  xp: 0,
  currentQuestionIndex: 0,
  shouldShowTimeTracker: true,
  startTime: 0,
  calculatorHistory: [],
}

const slice = createSlice({
  name: 'testPrep',
  initialState,
  reducers: {
    setFilterData: (state, action) => {
      state.filterData = action.payload
    },
    setQuestions: (state, action) => {
      const { id, testId, questions } = action.payload
      state.questions = questions
      state.id = id
      state.testId = testId
    },
    setResults: (state, action) => {
      const { results, xp, language } = action.payload
      state.results = results
      state.xp = xp
      state.resultsLanguage = language ?? null
    },
    setExamId: (state, action) => {
      state.id = action.payload
    },
    setCurrentQuestionIndex: (state, action) => {
      state.currentQuestionIndex = action.payload
    },
    setShouldShowTimeTracker: (state, action) => {
      state.shouldShowTimeTracker = action.payload
    },
    setStartTime: (state, action) => {
      state.startTime = action.payload
    },
    setUserInputForCurrentQuestion: (state, action) => {
      const { questionIndex, updatedQuestion } = action.payload
      state.questions[questionIndex] = updatedQuestion
    },
    setCalculatorHistory: (state, action) => {
      state.calculatorHistory = action.payload
    },
  },
})

export const selectFilterData = (state: { testPrep: TestPrepSliceType }): TestFilterType | null =>
  state.testPrep.filterData
export const selectQuestions = (state: { testPrep: TestPrepSliceType }): PracticeExercise[] | [] =>
  state.testPrep.questions
export const selectResults = (state: { testPrep: TestPrepSliceType }): ExamResults[] | [] => state.testPrep.results
export const selectResultsLanguage = (state: { testPrep: TestPrepSliceType }): AppLanguage | null =>
  state.testPrep.resultsLanguage
export const selectXp = (state: { testPrep: TestPrepSliceType }): number => state.testPrep.xp
export const selectExamId = (state: { testPrep: TestPrepSliceType }): number | null => state.testPrep.id
export const selectCurrentQuestionIndex = (state: { testPrep: TestPrepSliceType }): number =>
  state.testPrep.currentQuestionIndex
export const selectShouldShowTimeTracker = (state: { testPrep: TestPrepSliceType }): boolean =>
  state.testPrep.shouldShowTimeTracker
export const selectStartTime = (state: { testPrep: TestPrepSliceType }): number => state.testPrep.startTime
export const selectCalculatorHistory = (state: {
  testPrep: TestPrepSliceType
}): Array<{ expression: string; result: number }> => state.testPrep.calculatorHistory

export const {
  setFilterData: setFilterDataActionCreator,
  setQuestions: setQuestionsActionCreator,
  setResults: setResultsActionCreator,
  setExamId: setExamIdActionCreator,
  setShouldShowTimeTracker: setShouldShowTimeTrackerActionCreator,
  setStartTime: setStartTimeActionCreator,
  setCurrentQuestionIndex: setCurrentQuestionIndexActionCreator,
  setUserInputForCurrentQuestion: setUserInputForCurrentQuestionActionCreator,
  setCalculatorHistory: setCalculatorHistoryActionCreator,
} = slice.actions
export default slice.reducer
